import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { userService, UserProfile, UpdateProfileInput } from '../services/userService';
import { logger } from '../utils/logger';
import './Profile.css';

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [formData, setFormData] = useState<UpdateProfileInput>({
    displayName: '',
    bio: '',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getProfile();
      setProfile(data);
      setFormData({
        displayName: data.displayName || '',
        bio: data.bio || '',
      });
    } catch (err: any) {
      logger.error('Error loading profile:', err);
      setError(err.response?.data?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const updatedProfile = await userService.updateProfile(formData);
      setProfile(updatedProfile);
      setIsEditing(false);
      setSuccessMessage('Profile updated successfully!');

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      logger.error('Error updating profile:', err);
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        displayName: profile.displayName || '',
        bio: profile.bio || '',
      });
    }
    setIsEditing(false);
    setError(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
  };

  const handleUploadPhoto = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError(null);

      const { uploadUrl, key } = await userService.generateProfilePictureUploadUrl(
        selectedFile.name,
        selectedFile.type,
        selectedFile.size
      );

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      const validation = await userService.validateProfilePicture(key);

      if (!validation.isValid) {
        throw new Error('Image validation failed. Please upload an appropriate image.');
      }

      await loadProfile();

      setSuccessMessage('Profile picture updated successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);

      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (err: any) {
      logger.error('Error uploading photo:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!profile?.profilePictureUrl) return;

    if (!window.confirm('Are you sure you want to delete your profile picture?')) {
      return;
    }

    try {
      setUploading(true);
      setError(null);

      await userService.deleteProfilePicture();
      await loadProfile();

      setSuccessMessage('Profile picture removed successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      logger.error('Error deleting photo:', err);
      setError(err.response?.data?.message || err.message || 'Failed to delete photo');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="profile-loading">
          <div className="profile-loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container">
        <div className="profile-error">
          <h2 className="profile-error-title">Failed to Load Profile</h2>
          <p className="profile-error-text">{error || 'Something went wrong'}</p>
          <button onClick={loadProfile} className="profile-error-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '2rem 1rem', maxWidth: '800px', margin: '0 auto' }}>
      {/* Hero Section */}
      <div className="profile-hero">
        <div className="profile-hero-content">
          {/* Profile Picture */}
          <div className="profile-avatar-wrapper">
            {(previewUrl || profile.profilePictureUrl) ? (
              <img
                src={previewUrl || profile.profilePictureUrl}
                alt={profile.displayName || 'Profile'}
                className="profile-avatar"
              />
            ) : (
              <div className="profile-avatar-placeholder">
                {(profile.displayName?.[0] || profile.email[0]).toUpperCase()}
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="profile-info">
            <h1 className="profile-name">
              {profile.displayName || 'Your Profile'}
            </h1>
            <p className="profile-username">@{profile.username}</p>

            {/* Stats */}
            <div className="profile-stats">
              <div className="profile-stat">
                <span className="profile-stat-value">{profile.streamCount}</span>
                <span className="profile-stat-label">Streams</span>
              </div>
              <Link to={`/users/${profile.username}/followers`} className="profile-stat-link">
                <div className="profile-stat">
                  <span className="profile-stat-value">{profile.followerCount}</span>
                  <span className="profile-stat-label">Followers</span>
                </div>
              </Link>
              <Link to={`/users/${profile.username}/following`} className="profile-stat-link">
                <div className="profile-stat">
                  <span className="profile-stat-value">{profile.followingCount}</span>
                  <span className="profile-stat-label">Following</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="profile-message error">{error}</div>
      )}
      {successMessage && (
        <div className="profile-message success">{successMessage}</div>
      )}

      {/* Photo Upload Section */}
      <div className="profile-form-section">
        <h2 className="profile-form-title">Profile Picture</h2>

        <div className="profile-photo-upload">
          {(previewUrl || profile.profilePictureUrl) ? (
            <img
              src={previewUrl || profile.profilePictureUrl}
              alt="Profile preview"
              className="profile-photo-preview"
            />
          ) : (
            <div className="profile-photo-preview" style={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #A855F7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '2rem',
              fontWeight: 700
            }}>
              {(profile.displayName?.[0] || profile.email[0]).toUpperCase()}
            </div>
          )}

          <div className="profile-photo-upload-content">
            <p className="profile-photo-upload-title">
              {selectedFile ? selectedFile.name : 'Upload a new photo'}
            </p>
            <p className="profile-photo-upload-hint">
              JPG, PNG or GIF. Max 5MB.
            </p>
          </div>
        </div>

        <div className="profile-form-actions" style={{ marginTop: '1rem' }}>
          <label className="profile-form-btn primary" style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
            {selectedFile ? 'Change Photo' : 'Choose Photo'}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>

          {selectedFile && (
            <>
              <button
                onClick={handleUploadPhoto}
                disabled={uploading}
                className="profile-form-btn primary"
                style={{ background: '#22C55E' }}
              >
                {uploading ? 'Uploading...' : 'Save Photo'}
              </button>
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setPreviewUrl(null);
                }}
                disabled={uploading}
                className="profile-form-btn secondary"
              >
                Cancel
              </button>
            </>
          )}

          {!selectedFile && profile.profilePictureUrl && (
            <button
              onClick={handleDeletePhoto}
              disabled={uploading}
              className="profile-form-btn secondary"
              style={{ color: '#EF4444', borderColor: '#EF4444' }}
            >
              Delete Photo
            </button>
          )}
        </div>
      </div>

      {/* Profile Details Section */}
      <div className="profile-form-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="profile-form-title">Profile Details</h2>
          {!isEditing && (
            <button onClick={() => setIsEditing(true)} className="profile-form-btn primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '0.5rem' }}>
                <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Edit
            </button>
          )}
        </div>

        <div className="profile-form-group">
          <label className="profile-form-label">Email</label>
          <div className="profile-form-input" style={{
            background: 'var(--background-elevated)',
            cursor: 'not-allowed',
            opacity: 0.7
          }}>
            {profile.email}
          </div>
          <p className="profile-form-hint">Email cannot be changed</p>
        </div>

        <div className="profile-form-group">
          <label className="profile-form-label">Display Name</label>
          {isEditing ? (
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              className="profile-form-input"
              placeholder="Your display name"
            />
          ) : (
            <div className="profile-form-input" style={{ background: 'var(--background-elevated)' }}>
              {profile.displayName || <span style={{ opacity: 0.5 }}>Not set</span>}
            </div>
          )}
        </div>

        <div className="profile-form-group">
          <label className="profile-form-label">Bio</label>
          {isEditing ? (
            <>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="profile-form-textarea"
                placeholder="Tell us about yourself..."
                maxLength={500}
              />
              <p className="profile-form-hint">{(formData.bio || '').length}/500 characters</p>
            </>
          ) : (
            <div className="profile-form-input" style={{
              background: 'var(--background-elevated)',
              minHeight: '80px',
              whiteSpace: 'pre-wrap'
            }}>
              {profile.bio || <span style={{ opacity: 0.5 }}>No bio yet</span>}
            </div>
          )}
        </div>

        {isEditing && (
          <div className="profile-form-actions">
            <button
              onClick={handleSave}
              disabled={saving}
              className="profile-form-btn primary"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="profile-form-btn secondary"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="profile-form-section">
        <h2 className="profile-form-title">Quick Links</h2>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to={`/users/${profile.username}`} className="profile-form-btn secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '0.5rem' }}>
              <path d="M15 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            View Public Profile
          </Link>
          <Link to="/manage-streams" className="profile-form-btn secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '0.5rem' }}>
              <path d="M9 18V5L21 3V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="2"/>
              <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Manage Streams
          </Link>
          <Link to="/settings" className="profile-form-btn secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '0.5rem' }}>
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
              <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Settings
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Profile;
